import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { apiError, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Archive, Trash2, RotateCcw, X } from "lucide-react";
import { LongTextarea } from "@/components/LongText";
import { useAuth } from "@/context/AuthContext";
import { EliminaDefBtn, SvuotaCestinoBtn } from "@/components/CestinoDefinitivo";
import { BarraElenco, Casella, SelezionaTutto, azioniCestino, useSelezione } from "@/components/SelezioneFile";

const MOTIVI = [["fredda", "Fredda"], ["non_vende", "Non vende più"], ["venduto_altri", "Venduto con altri"], ["dati_sbagliati", "Dati sbagliati"], ["altro", "Altro"]];

const titoloNotizia = (n) => n.via_immobile || n.indirizzo || `${n.nome || ""} ${n.cognome || ""}`.trim() || "Notizia";

function Shell({ title, sub, children, footer, onClose, testid }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4" data-testid={testid}
      onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-none" />
      <div role="dialog" aria-modal="true" className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-[var(--border)]">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-bold">{title}</h3>
            {sub && <p data-testid={`${testid}-sub`} className="text-sm text-[var(--muted)] break-words">{sub}</p>}
          </div>
          <button data-testid={`${testid}-close`} onClick={onClose} aria-label="Chiudi" className="p-2 -mr-2 rounded-lg hover:bg-[#F7F7F8] shrink-0"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">{children}</div>
        <div className="px-5 py-4 border-t border-[var(--border)] flex gap-2">{footer}</div>
      </div>
    </div>,
    document.body,
  );
}

export function ArchiviaModal({ notizia, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [nota, setNota] = useState("");
  const [mesi, setMesi] = useState("");
  const [busy, setBusy] = useState(false);
  const salva = async () => {
    if (!motivo) return toast.error("Scegli il motivo");
    if (motivo === "altro" && !nota.trim()) return toast.error("Scrivi una nota");
    setBusy(true);
    try { await api.post(`/notizie/${notizia.id}/archivia`, { motivo, nota, ricontatta_mesi: mesi ? Number(mesi) : null }); toast.success("Notizia archiviata"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Archivia notizia" sub={titoloNotizia(notizia)} onClose={onClose} testid="archivia-modal"
      footer={<>
        <button data-testid="archivia-annulla" onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium">Annulla</button>
        <button data-testid="archivia-conferma" disabled={busy} onClick={salva} className="flex-1 py-2.5 rounded-xl bg-[#111111] text-white font-semibold text-sm disabled:opacity-50">Archivia</button>
      </>}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] mb-2">Motivo *</div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {MOTIVI.map(([k, l]) => (
          <button key={k} data-testid={`archivia-motivo-${k}`} onClick={() => setMotivo(k)}
            className={`py-2.5 px-3 rounded-xl border text-sm font-medium ${motivo === k ? "bg-[#111111] text-white border-[#111111]" : "border-[var(--border)] bg-white"}`}>{l}</button>
        ))}
      </div>
      <LongTextarea data-testid="archivia-nota" value={nota} onChange={(e) => setNota(e.target.value)} rows={2}
        placeholder={motivo === "altro" ? "Nota (obbligatoria)" : "Nota (facoltativa)"} className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] text-base sm:text-sm mb-3" />
      <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] mb-2">Ricontattare tra (facoltativo)</div>
      <div className="flex gap-2">
        {[["", "No"], ["3", "3 mesi"], ["6", "6 mesi"], ["12", "12 mesi"]].map(([v, l]) => (
          <button key={v || "no"} data-testid={`archivia-mesi-${v || "no"}`} onClick={() => setMesi(v)}
            className={`flex-1 py-2 rounded-xl border text-xs font-semibold ${mesi === v ? "bg-[#FC82ED] border-[#FC82ED] text-black" : "border-[var(--border)]"}`}>{l}</button>
        ))}
      </div>
    </Shell>
  );
}

export function EliminaModal({ notizia, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const ok = async () => {
    setBusy(true);
    try { await api.post(`/notizie/${notizia.id}/elimina`); toast.success("Notizia spostata nel cestino (30 giorni)"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Eliminare la notizia?" sub={titoloNotizia(notizia)} onClose={onClose} testid="elimina-notizia-modal"
      footer={<>
        <button data-testid="elimina-notizia-annulla" onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium">Annulla</button>
        <button data-testid="elimina-notizia-conferma" disabled={busy} onClick={ok} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-sm disabled:opacity-50">Elimina</button>
      </>}>
      <p className="text-sm text-slate-600">Va nel <b>Cestino Notizie</b>: puoi ripristinarla entro 30 giorni, poi viene cancellata definitivamente (dati personali compresi).</p>
    </Shell>
  );
}

export function AzioniNotizia({ notizia, onDone, compact }) {
  const [m, setM] = useState(null);
  const conv = notizia.stato === "incarico_acquisito" || notizia.immobile_id;
  const cls = compact ? "p-2 rounded-lg border border-[var(--border)] bg-white hover:bg-slate-50" : "flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-[#F7F7F8]";
  return (
    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      <button data-testid={`archivia-btn-${notizia.id}`} title="Archivia" onClick={() => setM("a")} className={`${cls} text-slate-700`}><Archive size={15} />{!compact && " Archivia"}</button>
      {!conv && <button data-testid={`elimina-btn-${notizia.id}`} title="Elimina" onClick={() => setM("e")} className={`${cls} text-red-500`}><Trash2 size={15} />{!compact && " Elimina"}</button>}
      {m === "a" && <ArchiviaModal notizia={notizia} onClose={() => setM(null)} onDone={() => { setM(null); onDone(); }} />}
      {m === "e" && <EliminaModal notizia={notizia} onClose={() => setM(null)} onDone={() => { setM(null); onDone(); }} />}
    </div>
  );
}

export function ArchivioView({ kind, q = "" }) {
  const [list, setList] = useState(null);
  const { isDirezione: _soc, isSegreteria: _seg } = useAuth();
  const isDirezione = _soc || _seg;
  const cestino = kind === "cestino";
  const S = useSelezione();
  const load = () => api.get(cestino ? "/notizie-cestino" : "/notizie-archiviate").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { setList(null); load(); }, [kind]);
  const azione = async (id) => {
    try { await api.post(cestino ? `/notizie-cestino/${id}/ripristina` : `/notizie-archiviate/${id}/riattiva`); toast.success(cestino ? "Notizia ripristinata" : "Notizia riattivata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!list) return <div className="skeleton h-40 rounded-2xl" />;
  const qq = q.trim().toLowerCase();
  const shown = qq ? list.filter((n) => `${n.titolo || ""} ${n.indirizzo || ""} ${n.agente || ""}`.toLowerCase().includes(qq)) : list;
  if (!shown.length) return <div className="card-immo p-10 text-center text-sm text-[var(--muted)]" data-testid={`${kind}-vuoto`}>{cestino ? "Il cestino è vuoto." : "Nessuna notizia archiviata."}</div>;
  return (
    <div className="space-y-2" data-testid={`${kind}-lista`}>
      {cestino && <div className="flex items-center gap-3 flex-wrap"><SelezionaTutto ids={shown.map((n) => n.id)} sel={S.sel} onTutti={S.tutti} testid="cestino-notizie-seltutto" /><p className="text-xs text-[var(--muted)] flex-1">Le notizie restano qui 30 giorni, poi vengono cancellate definitivamente.</p>{isDirezione && <SvuotaCestinoBtn ambito="notizie" count={list.length} onDone={load} />}</div>}
      {cestino && <BarraElenco sel={S.sel} onAnnulla={S.annulla} onFatto={load}
        azioni={azioniCestino((id) => api.post(`/notizie-cestino/${id}/ripristina`), isDirezione ? (id) => api.post(`/cestino/notizie/${id}/elimina-definitivo`, { conferma: "ELIMINA" }) : null)} />}
      {shown.map((n) => (
        <div key={n.id} data-testid={`${kind}-row-${n.id}`} className={`card-immo p-4 flex items-center gap-3 flex-wrap ${S.sel.has(n.id) ? "ring-2 ring-[#FC82ED]" : ""}`}>
          {cestino && <Casella inline on={S.sel.has(n.id)} onToggle={() => S.toggle(n.id)} testid={`cestino-notizia-sel-${n.id}`} />}
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm truncate">{n.titolo}</div>
            <div className="text-xs text-[var(--muted)] break-words">
              {n.indirizzo && `${n.indirizzo} · `}{n.agente}
              {!cestino && n.archivio && <> · <b>{n.motivo_label}</b>{n.archivio.nota ? ` — ${n.archivio.nota}` : ""} · {fmtDate(n.archivio.at)}{n.archivio.ricontatta_il && ` · ricontatto il ${fmtDate(n.archivio.ricontatta_il)}`}</>}
              {cestino && n.cestino && <> · eliminata il {fmtDate(n.cestino.at)} · cancellazione il {fmtDate(n.cestino.scade_il)}</>}
            </div>
          </div>
          <button data-testid={`${kind}-azione-${n.id}`} onClick={() => azione(n.id)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#111111] text-white text-xs font-semibold"><RotateCcw size={14} /> {cestino ? "Ripristina" : "Riattiva"}</button>
          {cestino && isDirezione && <EliminaDefBtn ambito="notizie" id={n.id} onDone={load} testid={`cestino-elimina-def-${n.id}`} />}
        </div>
      ))}
    </div>
  );
}
